import type { AslDictionaryResult, Word } from "../shared-types";

/** Poll the existing ASL lookup while its shared provider catalogue warms up. */
async function waitForAsl<T>(
  request: <T>(path: string) => Promise<T>,
  path: string,
  signal: AbortSignal,
): Promise<T> {
  const deadline = Date.now() + 65000;
  while (!signal.aborted) {
    const result = await request<T & { pending?: boolean }>(path);
    if (signal.aborted) throw new Error("aborted");
    if (!result.pending) return result;
    if (Date.now() >= deadline) throw new Error("asl_unavailable");
    await new Promise<void>((resolve, reject) => {
      const abort = () => {
        clearTimeout(timer);
        reject(new Error("aborted"));
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", abort);
        resolve();
      }, 1000);
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) abort();
    });
  }
  throw new Error("aborted");
}

export function loadAslDictionary(
  request: <T>(path: string) => Promise<T>,
  query: string,
  signal: AbortSignal,
) {
  return waitForAsl<AslDictionaryResult>(
    request,
    `asl/clip?${new URLSearchParams({ q: query })}`,
    signal,
  );
}

export function loadAslWords(
  request: <T>(path: string) => Promise<T>,
  search: Record<string, string>,
  signal: AbortSignal,
) {
  return waitForAsl<Word[]>(
    request,
    `words?${new URLSearchParams({ ...search, language: "asl", source: "voisea" })}`,
    signal,
  );
}
